import React from 'react'

import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import styled from 'styled-components'

import { fetchAlerts } from 'actions/cms/alerts'
import { checkIsActiveSliderContent } from 'actions/cms/slider'
import { resetFlightsOffers } from 'actions/flightsOffers'
import { resetFlightStatusSearch } from 'actions/flightStatusSearch'
import { clearPaymentOptionsError } from 'actions/paymentOptions'
import { clearReservationError, clearReservation } from 'actions/reservation'
import { clearDocumentTypesRulesError } from 'actions/rules/checkout/documentTypes'
import { clearPassengerDataError } from 'actions/rules/checkout/passengerData'
import { clearSpecialRequestsError } from 'actions/rules/checkout/specialRequests'
import { fetchSuggestions } from 'actions/suggestions'
import BanksSlider from 'components/banks-slider'
import Carousel from 'components/carousel'
import Headline from 'components/headline'
import HelpCenter from 'components/help-center'
import HomeInfo from 'components/home-info'
import { PageBlock } from 'components/layout'
import { TransitionLoader } from 'components/loaders'
import PopularDestinations from 'components/popular-destinations'
import { loadRecaptcha } from 'components/recaptchaV2'
import SearchBox from 'components/search-box'
import SearchBoxContainer from 'components/search-box-container'
import AlertsContainer from 'containers/alerts'
import StickyWhatsapp from 'containers/home'
import { getCurrentUrl } from 'helpers/googleTagManager'
import { isHomeRouteFromMatch } from 'helpers/routeMatchers'
import { removeBookingSessionInfo } from 'helpers/sessionStorage'
import {
  generateHomeRoute,
  generatePressCommunicationRoute,
  generateReservationRoute
} from 'helpers/url/localization'
import { getSearchBoxTab } from 'helpers/url/parseUrl'
import { buildSearchBoxTabs, isEmpty, isMobile, setArtId } from 'helpers/utils'
import withErrorHandler from 'hoc/withErrorHandler'
import { getAlertsData } from 'selectors/cms/alerts'
import { getDarkSiteData, isEnabledDarkSite, getDarkSiteField } from 'selectors/cms/darkSite'
import { getMainSlidersData, getSlidersData } from 'selectors/cms/slider'
import { mapMediaType } from 'selectors/mediaType'
import {
  getReservationData,
  isFetchingEmptyReservation,
  getReservationLoadingError
} from 'selectors/reservation'

const TeasersWrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.alabaster};
`

class HomePage extends React.Component {
  state = {
    activeTab: null,
    dynamicMargin: -230 //Respect already chosen css prop
  }
  //Refs to calc search box margin layout.
  wrapperRef = React.createRef(null)
  infoRef = React.createRef(null)
  listenerCmsContent = null

  dynamicMarginCalculation = () => {
    this.infoRef &&
      this.wrapperRef &&
      this.setState(prevState => ({
        ...prevState,
        dynamicMargin:
          -1 *
          (this.wrapperRef.current.clientHeight -
            this.infoRef.current.clientHeight -
            this.infoRef.current.offsetTop -
            30) //Adds 30px separation between info container and search box container.
      }))
  }

  componentWillUnmount = () => {
    /**
     * Clears darkSite filter workaround.
     * TODO: Fix SSR solution or maybe stop client routing clearing body inline style.
     * */
    if (this.props.isDarkSiteEnabled && isHomeRouteFromMatch(this.props.match)) {
      const body = window && window.document.querySelector('body')
      if (body) {
        body.style.filter = 'none'
      }
      window.removeEventListener('resize')
    }

    clearInterval(this.listenerCmsContent)
  }

  componentDidMount = () => {
    loadRecaptcha(this.props.i18n.language)

    /**
     * Due to some SSR or client routing rare thing, changing route removes already set body color,
     * this worksaround that to still show gray filter.
     *
     * TODO: Fix SSR solution or maybe stop client routing clearing body inline style.
     * */
    if (this.props.isDarkSiteEnabled && isHomeRouteFromMatch(this.props.match)) {
      const body = window && window.document.querySelector('body')
      if (body) {
        body.style.filter = 'grayScale(100%)'
      }
      /**
       * Calc dynamic margin
       */
      this.dynamicMarginCalculation()
      window.addEventListener('resize', this.dynamicMarginCalculation)
    }

    const { dataLayer } = window

    this.scrollToComponent = require('react-scroll-to-component')

    const { language } = this.props.i18n

    dataLayer?.push({
      event: 'page_load',
      Pais: language && language,
      URL: getCurrentUrl()
    })

    this.props.resetFlightsOffers()
    this.props.fetchAlerts()
    this.props.resetFlightStatusSearch()
    this.props.clearReservation()
    // remove booking token when entering url manually
    removeBookingSessionInfo()

    this.setTabAndScroll()
    this.setListenerCmcContent()
  }

  componentDidUpdate = prevProps => {
    if (!prevProps.reservation && !isEmpty(this.props.reservation)) {
      this.props.history.push(generateReservationRoute(this.props.i18n.language))
    }

    if (prevProps.location.search !== this.props.location.search) {
      setArtId(this.props.location)
    }
  }

  generatePathSearchBoxTab = (index, language) => ({
    pathname: generateHomeRoute(language),
    search:
      index &&
      `?activeTab=${
        Object.entries(buildSearchBoxTabs(language)).find(tab => tab[1]?.index === index)[1]?.url
      }`
  })

  setTabAndScroll = () => {
    const {
      history,
      i18n,
      location,
      location: { search }
    } = this.props
    const url = search.split('?').pop()
    const tabIndex = url && getSearchBoxTab(url, i18n.language)?.index

    if (url) {
      if (url.indexOf('activeTab=') >= 0) {
        history.replace(this.generatePathSearchBoxTab(tabIndex, i18n.language))
      }
      setTimeout(() => setArtId(location))
    }

    this.setState({
      activeTab: tabIndex || 0
    })
    this.scrollToSearchBoxContainer()
  }

  scrollToSearchBoxContainer = () => {
    this.scrollToComponent(document.querySelector('.searchBoxContainer'), {
      offset: -100,
      align: 'top',
      duration: 800,
      ease: 'inOutQuad'
    })
  }

  getCarouselData = () => {
    if (this.props.isDarkSiteEnabled) {
      return [
        {
          ...this.props.darkSiteField,
          buttonName: this.props.t(this.props.darkSiteField.buttonName),
          buttonUrl: generatePressCommunicationRoute(this.props.i18n.language)
        }
      ]
    }
    return !isEmpty(this.props.sliders.data) ? this.props.sliders.data : [{ image: '' }]
  }

  onTabClick = activeTab => {
    this.setState({ activeTab }, this.handleReserveError)
  }

  handleReserveError = () => {
    if (this.props.reservationLoadingError) {
      this.props.clearReservationError()
      this.props.clearPaymentOptionsError()
      this.props.clearDocumentTypesRulesError()
      this.props.clearPassengerDataError()
      this.props.clearSpecialRequestsError()
    }
  }

  setListenerCmcContent = () => {
    const { originalSliders, sliders, checkIsActiveSliderContent } = this.props
    checkIsActiveSliderContent && checkIsActiveSliderContent(originalSliders || [], sliders || [])
    this.listenerCmsContent = setInterval(() => {
      checkIsActiveSliderContent && checkIsActiveSliderContent(originalSliders || [], sliders || [])
    }, 5000)
  }

  render = () => (
    <PageBlock>
      {(this.props.isFetchingPossiblyEmptyReservation || this.state.activeTab === null) && (
        <TransitionLoader />
      )}
      {!this.props.isDarkSiteEnabled && <AlertsContainer alerts={this.props.alerts} />}
      <Carousel
        data={this.getCarouselData()}
        isMobile={this.props.mobile}
        withSearchBox
        wrapperRef={this.wrapperRef}
        infoRef={this.infoRef}
        isDarkSiteEnabled={this.props.isDarkSiteEnabled}
      />
      <SearchBoxContainer
        onCheckInNavigation={url => window.open(url, '_blank')}
        searchBox={<SearchBox />}
        selectedTab={this.state.activeTab}
        onTabClick={this.onTabClick}
        handleReserveError={this.handleReserveError}
        dynamicMargin={this.props.isDarkSiteEnabled && this.state.dynamicMargin}
      />
      {!this.props.isDarkSiteEnabled && (
        <TeasersWrapper>
          <BanksSlider isMobile={this.props.mobile} />
          {this.props.t('general.footer.best-promotions') ? (
            <Headline level={2} titleHtml={this.props.t('general.footer.best-promotions')} />
          ) : null}
          <PopularDestinations />
          <HomeInfo withIcons />
          <HelpCenter />
        </TeasersWrapper>
      )}

      {<StickyWhatsapp />}
    </PageBlock>
  )
}

const mapDispatchToProps = {
  resetFlightsOffers,
  fetchSuggestions,
  fetchAlerts,
  resetFlightStatusSearch,
  clearReservationError,
  clearPaymentOptionsError,
  clearDocumentTypesRulesError,
  clearPassengerDataError,
  clearSpecialRequestsError,
  clearReservation,
  checkIsActiveSliderContent
}

const mapStateToProps = state => ({
  alerts: getAlertsData(state),
  sliders: getMainSlidersData(state),
  originalSliders: getSlidersData(state),
  darkSite: getDarkSiteData(state),
  isDarkSiteEnabled: isEnabledDarkSite(state),
  reservationLoadingError: getReservationLoadingError(state),
  isFetchingPossiblyEmptyReservation: isFetchingEmptyReservation(state),
  reservation: getReservationData(state),
  darkSiteField: getDarkSiteField(state),
  mobile: isMobile(mapMediaType(state))
})

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(withErrorHandler(HomePage))
)
